import * as pdfjsLib from "https://cdn.jsdelivr.net/npm/pdfjs-dist@6.1.200/build/pdf.min.mjs";
import { PDFDocument, PDFName, StandardFonts } from "https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/+esm";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdn.jsdelivr.net/npm/pdfjs-dist@6.1.200/build/pdf.worker.min.mjs";

const REQUIRED_PDF_FIELDS = [
  "STORE NAME  DOING BUSINESS AS",
  "STORE ADDRESS",
  "CITY  STATE  ZIP",
  "PHONE",
  "SHOP MANAGER NAME AND CONTACT INFO",
  "ACCOUNTS PAYABLE MANAGER",
  "ACCOUNTS PAYABLE MANAGER PHONE  EMAIL",
  "DATE",
];

const EXCLUSIVE_GROUPS = [
  ["CHECK", "ACHWIRE TRANSFER"],
  ["OPTICAL CHAIN", "INDEPENDENT", "DOCTOR", "FASHION DOOROTHER"],
];

const SAME_ADDRESS_FIELDS = {
  "TICK BOX IF SAME AS STORE ADDRESS ABOVE": ["SHIP TO ADDRESS", "CITY  STATE  ZIP_2"],
  "TICK BOX IF SAME AS STORE ADDRESS ABOVE_2": ["BILL TO ADDRESS", "CITY  STATE  ZIP_3"],
};

const SIGNATURE_FIELD = "Signature3_es_:signer:signature";

document.querySelectorAll("[data-tejesta-pdf-form]").forEach((root) => {
  if (root.dataset.initialized === "true") return;
  root.dataset.initialized = "true";
  initApplication(root).catch((error) => {
    console.error("TEJESTA PDF form initialization failed", error);
    setStatus(root, "The application could not be loaded. Please refresh and try again.", "error");
  });
});

async function initApplication(root) {
  const configElement = root.querySelector("[data-tejesta-config]");
  const config = JSON.parse(configElement?.textContent || "{}");
  const viewer = root.querySelector("[data-pdf-viewer]");
  const submitButton = root.querySelector("[data-submit]");
  const state = {
    pdfBytes: null,
    pdf: null,
    values: new Map(),
    controls: new Map(),
    signatureDataUrl: "",
    signaturePlacement: null,
    rendering: false,
    downloadUrl: "",
  };

  if (!config.pdfUrl) {
    viewer.innerHTML = "<p>This section needs a PDF URL in the theme editor.</p>";
    viewer.setAttribute("aria-busy", "false");
    submitButton.disabled = true;
    return;
  }

  const submissionReady = Boolean(config.endpoint && config.turnstileEnabled);
  setupSignatureDialog(root, state);

  if (submissionReady) {
    submitButton.addEventListener("click", () => submitApplication(root, config, state));
  } else {
    submitButton.disabled = true;
  }

  const response = await fetch(config.pdfUrl, { credentials: "omit", mode: "cors" });
  if (!response.ok) throw new Error(`PDF request failed with ${response.status}`);

  state.pdfBytes = new Uint8Array(await response.arrayBuffer());
  if (new TextDecoder().decode(state.pdfBytes.slice(0, 5)) !== "%PDF-") {
    throw new Error("Configured file is not a PDF");
  }

  state.pdf = await pdfjsLib.getDocument({ data: state.pdfBytes.slice() }).promise;
  state.values.set("DATE", new Date().toLocaleDateString("en-US"));
  state.values.set("CHECK", false);
  state.values.set("ACHWIRE TRANSFER", false);

  await renderDocument(root, viewer, state);
  observeViewerWidth(root, viewer, state);

  setStatus(
    root,
    submissionReady ? "" : "Email delivery is not configured yet.",
    submissionReady ? "" : "error",
  );
}

function observeViewerWidth(root, viewer, state) {
  if (!("ResizeObserver" in window)) return;
  let previousWidth = viewer.clientWidth;
  let timer = 0;

  const observer = new ResizeObserver(() => {
    const nextWidth = viewer.clientWidth;
    if (Math.abs(nextWidth - previousWidth) < 8) return;
    previousWidth = nextWidth;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => renderDocument(root, viewer, state), 120);
  });

  observer.observe(viewer);
}

async function renderDocument(root, viewer, state) {
  if (state.rendering) return;
  state.rendering = true;
  viewer.setAttribute("aria-busy", "true");
  viewer.innerHTML = "";
  state.controls.clear();
  state.signaturePlacement = null;

  try {
    const firstPage = await state.pdf.getPage(1);
    const availableWidth = Math.max(320, viewer.clientWidth - 36);
    const unscaled = firstPage.getViewport({ scale: 1 });
    const fitScale = Math.min(1.35, Math.max(0.8, availableWidth / unscaled.width));
    const scale = fitScale;

    for (let pageNumber = 1; pageNumber <= state.pdf.numPages; pageNumber += 1) {
      const page = await state.pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale });
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

      const pageElement = document.createElement("div");
      pageElement.className = "tejesta-pdf-form__page";
      pageElement.style.width = `${viewport.width}px`;
      pageElement.style.height = `${viewport.height}px`;
      pageElement.setAttribute("aria-label", `Page ${pageNumber} of ${state.pdf.numPages}`);

      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width * pixelRatio);
      canvas.height = Math.floor(viewport.height * pixelRatio);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      pageElement.append(canvas);

      const overlay = document.createElement("div");
      overlay.className = "tejesta-pdf-form__overlay";
      pageElement.append(overlay);
      viewer.append(pageElement);

      await page.render({
        canvasContext: canvas.getContext("2d", { alpha: false }),
        viewport,
        transform: pixelRatio === 1 ? null : [pixelRatio, 0, 0, pixelRatio, 0, 0],
      }).promise;

      const annotations = await page.getAnnotations({ intent: "display" });
      for (const annotation of annotations) {
        addAnnotationControl(root, overlay, annotation, viewport, pageNumber - 1, state);
      }
    }
  } finally {
    viewer.setAttribute("aria-busy", "false");
    state.rendering = false;
  }
}

function addAnnotationControl(root, overlay, annotation, viewport, pageIndex, state) {
  const name = annotation.fieldName;
  if (!name || !annotation.rect) return;

  const [rawX1, rawY1] = transformPoint(annotation.rect[0], annotation.rect[1], viewport.transform);
  const [rawX2, rawY2] = transformPoint(annotation.rect[2], annotation.rect[3], viewport.transform);
  const left = Math.min(rawX1, rawX2);
  const top = Math.min(rawY1, rawY2);
  const width = Math.abs(rawX2 - rawX1);
  const height = Math.abs(rawY2 - rawY1);

  if (name === SIGNATURE_FIELD) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "tejesta-pdf-signature";
    positionControl(button, left, top, width, height);
    button.setAttribute("aria-label", "Add signature");
    updateSignaturePreview(button, state.signatureDataUrl);
    button.addEventListener("click", () => state.openSignatureDialog());
    overlay.append(button);
    state.controls.set(name, button);
    state.signaturePlacement = { pageIndex, rect: annotation.rect.map(Number) };
    return;
  }

  const isCheckbox = annotation.fieldType === "Btn" && annotation.checkBox !== false;
  const control = document.createElement("input");
  control.className = "tejesta-pdf-field";
  control.type = isCheckbox ? "checkbox" : "text";
  control.name = name;
  control.setAttribute("aria-label", readableFieldName(name));
  control.dataset.pdfField = name;

  if (isCheckbox) {
    control.checked = state.values.get(name) === true;
    positionControl(control, left, top, Math.max(width, 14), Math.max(height, 14));
  } else {
    control.value = String(state.values.get(name) || "");
    control.autocomplete = "off";
    control.maxLength = 300;
    if (REQUIRED_PDF_FIELDS.includes(name)) control.required = true;
    positionControl(control, left, top, width, height);
  }

  control.addEventListener("change", () => {
    state.values.set(name, isCheckbox ? control.checked : control.value.trim());
    if (isCheckbox && control.checked) enforceExclusiveGroup(name, state);
    if (isCheckbox && control.checked && SAME_ADDRESS_FIELDS[name]) copyStoreAddress(name, state);
  });

  control.addEventListener("input", () => {
    if (!isCheckbox) state.values.set(name, control.value);
  });

  overlay.append(control);
  state.controls.set(name, control);
}

function transformPoint(x, y, matrix) {
  const [a, b, c, d, e, f] = matrix;
  return [a * x + c * y + e, b * x + d * y + f];
}

function positionControl(control, left, top, width, height) {
  control.style.left = `${left}px`;
  control.style.top = `${top}px`;
  control.style.width = `${width}px`;
  control.style.height = `${height}px`;
}

function enforceExclusiveGroup(changedName, state) {
  const group = EXCLUSIVE_GROUPS.find((names) => names.includes(changedName));
  if (!group) return;

  for (const name of group) {
    if (name === changedName) continue;
    state.values.set(name, false);
    const control = state.controls.get(name);
    if (control instanceof HTMLInputElement) control.checked = false;
  }
}

function copyStoreAddress(checkboxName, state) {
  const [addressTarget, cityTarget] = SAME_ADDRESS_FIELDS[checkboxName];
  copyFieldValue("STORE ADDRESS", addressTarget, state);
  copyFieldValue("CITY  STATE  ZIP", cityTarget, state);
}

function copyFieldValue(sourceName, targetName, state) {
  const value = String(state.values.get(sourceName) || "");
  state.values.set(targetName, value);
  const target = state.controls.get(targetName);
  if (target instanceof HTMLInputElement) target.value = value;
}

function setupSignatureDialog(root, state) {
  const dialog = root.querySelector("[data-signature-dialog]");
  const canvas = root.querySelector("[data-signature-canvas]");
  const context = canvas.getContext("2d");
  let drawing = false;
  let hasInk = false;

  const resizeCanvas = () => {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 2.2;
    context.strokeStyle = "#111";
  };

  state.openSignatureDialog = () => {
    dialog.showModal();
    requestAnimationFrame(resizeCanvas);
  };

  const point = (event) => {
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  canvas.addEventListener("pointerdown", (event) => {
    drawing = true;
    hasInk = true;
    canvas.setPointerCapture(event.pointerId);
    const start = point(event);
    context.beginPath();
    context.moveTo(start.x, start.y);
  });

  canvas.addEventListener("pointermove", (event) => {
    if (!drawing) return;
    const next = point(event);
    context.lineTo(next.x, next.y);
    context.stroke();
  });

  const stopDrawing = () => {
    drawing = false;
  };

  canvas.addEventListener("pointerup", stopDrawing);
  canvas.addEventListener("pointercancel", stopDrawing);

  root.querySelector("[data-signature-clear]").addEventListener("click", () => {
    context.clearRect(0, 0, canvas.width, canvas.height);
    hasInk = false;
  });

  root.querySelector("[data-signature-cancel]").addEventListener("click", () => dialog.close());

  root.querySelector("[data-signature-save]").addEventListener("click", () => {
    if (!hasInk) {
      setStatus(root, "Please sign inside the signature box.", "error");
      return;
    }
    state.signatureDataUrl = canvas.toDataURL("image/png");
    updateSignaturePreview(state.controls.get(SIGNATURE_FIELD), state.signatureDataUrl);
    dialog.close();
    setStatus(root, "Signature added.", "success");
  });
}

function updateSignaturePreview(control, dataUrl) {
  if (!control) return;
  control.replaceChildren();

  if (!dataUrl) {
    control.textContent = "Click to sign";
    return;
  }

  const image = document.createElement("img");
  image.src = dataUrl;
  image.alt = "Signature added";
  control.append(image);
}

async function submitApplication(root, config, state) {
  const submitButton = root.querySelector("[data-submit]");
  let completedPdf = null;

  syncVisibleValues(state);
  const firstMissing = REQUIRED_PDF_FIELDS.find((name) => !String(state.values.get(name) || "").trim());
  const contactEmail = extractContactEmail(state);

  if (!contactEmail) {
    setStatus(root, "Please include a valid email in Accounts Payable Manager Phone / Email.", "error");
    state.controls.get("ACCOUNTS PAYABLE MANAGER PHONE  EMAIL")?.focus();
    return;
  }

  if (firstMissing) {
    setStatus(root, `Please complete: ${readableFieldName(firstMissing)}.`, "error");
    state.controls.get(firstMissing)?.focus();
    return;
  }

  if (config.requireSignature && !state.signatureDataUrl) {
    setStatus(root, "Please add the store owner or buyer signature.", "error");
    state.controls.get(SIGNATURE_FIELD)?.focus();
    return;
  }

  if (config.turnstileEnabled && !root.querySelector('[name="cf-turnstile-response"]')?.value) {
    setStatus(root, "Please complete the security verification.", "error");
    return;
  }

  submitButton.disabled = true;
  setStatus(root, "Preparing your completed application…", "");

  try {
    const storeName = String(state.values.get("STORE NAME  DOING BUSINESS AS") || "TEJESTA account");
    const submissionId = crypto.randomUUID();

    completedPdf = await generateCompletedPdf(state);
    updateDownloadLink(root, state, completedPdf);

    const body = new FormData();
    body.append("pdf", completedPdf, `${slugify(storeName)}-new-account-application.pdf`);
    body.append("contact_email", contactEmail);
    body.append("store_name", storeName);
    body.append("consent", "true");
    body.append("submission_id", submissionId);
    body.append("cf-turnstile-response", root.querySelector('[name="cf-turnstile-response"]')?.value || "");
    body.append("website", root.querySelector('[name="website"]')?.value || "");

    setStatus(root, "Sending application…", "");
    const response = await fetch(config.endpoint, { method: "POST", body });
    const result = await response.json().catch(() => ({}));

    if (!response.ok || result.ok !== true) {
      throw new Error(result.error || "The application could not be delivered.");
    }

    setStatus(
      root,
      `Application sent successfully. Reference: ${result.submission_id || submissionId}.`,
      "success",
      true,
    );
  } catch (error) {
    console.error("TEJESTA application submission failed", error);
    setStatus(
      root,
      `${error.message || "Submission failed"}${completedPdf ? " Your completed PDF is available to download below." : ""}`,
      "error",
      Boolean(completedPdf),
    );
  } finally {
    submitButton.disabled = false;
    if (window.turnstile && config.turnstileEnabled) window.turnstile.reset();
  }
}

function extractContactEmail(state) {
  const sources = [
    state.values.get("ACCOUNTS PAYABLE MANAGER PHONE  EMAIL"),
    state.values.get("SHOP MANAGER NAME AND CONTACT INFO"),
  ];

  for (const source of sources) {
    const match = String(source || "").match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    if (match) return match[0].toLowerCase();
  }

  return "";
}

function syncVisibleValues(state) {
  for (const [name, control] of state.controls) {
    if (!(control instanceof HTMLInputElement)) continue;
    state.values.set(name, control.type === "checkbox" ? control.checked : control.value.trim());
  }
}

async function generateCompletedPdf(state) {
  const document = await PDFDocument.load(state.pdfBytes.slice());
  const form = document.getForm();
  const font = await document.embedFont(StandardFonts.Helvetica);

  for (const [name, value] of state.values) {
    if (name === SIGNATURE_FIELD) continue;
    const field = getField(form, name);
    if (!field) continue;

    if (typeof field.setText === "function") {
      field.setText(String(value || ""));
      try {
        field.setFontSize(9);
      } catch {}
    } else if (typeof field.check === "function") {
      value === true ? field.check() : field.uncheck();
    }
  }

  const signatureField = getField(form, SIGNATURE_FIELD);
  if (signatureField && typeof signatureField.setText === "function") signatureField.setText("");

  form.updateFieldAppearances(font);
  form.flatten();
  document.catalog.delete(PDFName.of("AcroForm"));

  if (state.signatureDataUrl && state.signaturePlacement) {
    const signature = await document.embedPng(state.signatureDataUrl);
    const [x1, y1, x2, y2] = state.signaturePlacement.rect;

    document.getPages()[state.signaturePlacement.pageIndex].drawImage(signature, {
      x: Math.min(x1, x2) + 2,
      y: Math.min(y1, y2) + 2,
      width: Math.abs(x2 - x1) - 4,
      height: Math.abs(y2 - y1) - 4,
    });
  }

  const bytes = await document.save({ useObjectStreams: false });
  return new Blob([bytes], { type: "application/pdf" });
}

function getField(form, name) {
  try {
    return form.getField(name);
  } catch {
    return null;
  }
}

function updateDownloadLink(root, state, blob) {
  if (state.downloadUrl) URL.revokeObjectURL(state.downloadUrl);
  state.downloadUrl = URL.createObjectURL(blob);

  const status = root.querySelector("[data-status]");
  let link = status.querySelector("[data-download]");

  if (!link) {
    link = document.createElement("a");
    link.className = "tejesta-pdf-form__download";
    link.dataset.download = "";
    link.textContent = "Download completed PDF";
    link.download = "tejesta-new-account-application.pdf";
    link.hidden = true;
    status.append(document.createElement("br"), link);
  }

  link.href = state.downloadUrl;
}

function setStatus(root, message, stateName, showDownload = false) {
  const status = root.querySelector("[data-status]");
  const link = status.querySelector("[data-download]");
  const savedLink = link?.cloneNode(true);

  status.textContent = message;
  status.dataset.state = stateName;

  if (savedLink) {
    savedLink.hidden = !showDownload;
    status.append(document.createElement("br"), savedLink);
  }
}

function readableFieldName(name) {
  const overrides = {
    "STORE NAME  DOING BUSINESS AS": "Store name / doing business as",
    "CITY  STATE  ZIP": "Store city / state / ZIP",
    "ACCOUNTS PAYABLE MANAGER PHONE  EMAIL": "Accounts payable manager phone / email",
    "ACHWIRE TRANSFER": "ACH / wire transfer",
    "FASHION DOOROTHER": "Fashion door / other",
  };

  return overrides[name] || name.replace(/_/g, " ").replace(/\s+/g, " ").toLowerCase();
}

function slugify(value) {
  return String(value || "tejesta")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 70) || "tejesta";
}